import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import { NativeSelect } from "../../ui/UIElements/Select";
import { Switch } from "../../ui/UIElements/Switch";
import { CampaignDatePicker } from "../../ui/Campaigns/CampaignDatePicker";
import { toast } from "sonner";
import {
  useCreateCampaign,
  useCampaignAudience,
} from "../../../hooks/useCampaigns";
import { useAiAgents } from "../../../hooks/useAiAgents";
import { getFinitlessTemporalChatUrl } from "../../../lib/linkInBio";
import "./campaigns-ordering.css";

const TEMPLATES = [
  {
    id: "newsletter",
    name: "Weekly Newsletter",
    subject: "This Week's Top Picks just for you! 📰",
    body: "Hi there! 👋\n\nWe have updated our menu with some exciting new additions that we think you will love. 🍔🍕🥗\n\nCome visit us this weekend! 🎉",
  },
  {
    id: "sale",
    name: "Flash Sale Alert",
    subject: "FLASH SALE: 50% OFF Everything! ⚡",
    body: "Hurry! ⏰ For the next 24 hours only, get 50% off all combos.\n\n🔥 Don't miss out on this exclusive offer! 🔥",
  },
  {
    id: "welcome",
    name: "Welcome Series",
    subject: "Welcome to the Finitless Family! ❤️",
    body: "We are thrilled to have you with us! 🎊\n\nAs a new member, you get access to exclusive deals and priority delivery. 🚀\n\nEnjoy your first order! 🛍️",
  },
];

const getAgentUrl = (agent) => {
  if (!agent?.site) return null;
  const site = agent.site;
  if (site?.domain && site?.ssl_status === "issued") {
    return `https://${site.domain}`;
  }
  if (site?.temporal_domain) {
    return getFinitlessTemporalChatUrl(site.temporal_domain);
  }
  return null;
};

const PRESET_COLORS = ["#8b5cf6", "#000000", "#10b981", "#ef4444", "#f59e0b"];

const RULE_LIST = [
  {
    key: "orders_count",
    title: "Order History",
    description: "Target customers based on purchase count",
    icon: "Users",
  },
  {
    key: "user_created_at",
    title: "Registration Date",
    description: "Target new or long-time users",
    icon: "UserPlus",
  },
  {
    key: "user_last_open_cart_at",
    title: "Open Carts",
    description: "Target users with items left in cart",
    icon: "ShoppingBag",
  },
];

const CreateCampaignPage = () => {
  const { t } = useTranslation("campaigns");
  const navigate = useNavigate();
  const createCampaignMutation = useCreateCampaign();
  const { data: agentsData } = useAiAgents({
    enabledFilter: "enabled",
    pageSize: 50,
  });

  const destinationUrls = useMemo(() => {
    const urls = [];
    if (agentsData?.agents) {
      agentsData.agents.forEach((agent) => {
        const url = getAgentUrl(agent);
        if (url) {
          urls.push(url);
        }
      });
    }
    return urls;
  }, [agentsData]);

  const [campaignName, setCampaignName] = useState("");
  const [audienceType, setAudienceType] = useState("fixed");
  const [isEnabled, setIsEnabled] = useState(true);
  const [isASAP, setIsASAP] = useState(true);
  const [scheduledAt, setScheduledAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [rules, setRules] = useState({
    orders_count: { active: false, value: "1", condition: ">" },
    user_created_at: {
      active: false,
      days: "30",
      date_from: "",
      date_to: "",
      date_condition: "<",
    },
    user_last_open_cart_at: {
      active: false,
      days: "7",
      date_from: "",
      date_to: "",
      date_condition: "<",
    },
  });
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [targetUrl, setTargetUrl] = useState("");
  const [buttonText, setButtonText] = useState(t("createPage.preview.shopNow"));
  const [buttonColor, setButtonColor] = useState("#8b5cf6");

  useEffect(() => {
    if (destinationUrls.length > 0 && !targetUrl) {
      setTargetUrl(destinationUrls[0]);
    }
  }, [destinationUrls, targetUrl]);

  useEffect(() => {
    if (TEMPLATES.length > 0 && !selectedTemplate) {
      applyTemplate(TEMPLATES[0].id);
    }
  }, []);

  const getDaysAgoDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(date.getDate()).padStart(2, "0")} 00:00:00`;
  };

  const formatDateForApi = (dateStr) => `${dateStr} 00:00:00`;

  const conditions = useMemo(() => {
    const result = [];
    if (rules.orders_count.active && rules.orders_count.value) {
      result.push({
        type: "orders_count",
        condition: rules.orders_count.condition,
        value: parseInt(rules.orders_count.value) || 0,
      });
    }
    if (rules.user_created_at.active) {
      if (rules.user_created_at.date_condition === "><") {
        if (rules.user_created_at.date_from && rules.user_created_at.date_to) {
          result.push({
            type: "user_created_at",
            date_condition: "><",
            date: formatDateForApi(rules.user_created_at.date_from),
            max_date: formatDateForApi(rules.user_created_at.date_to),
          });
        }
      } else if (rules.user_created_at.days) {
        result.push({
          type: "user_created_at",
          date_condition: rules.user_created_at.date_condition,
          date: getDaysAgoDate(parseInt(rules.user_created_at.days) || 0),
        });
      }
    }
    if (rules.user_last_open_cart_at.active) {
      if (rules.user_last_open_cart_at.date_condition === "><") {
        if (
          rules.user_last_open_cart_at.date_from &&
          rules.user_last_open_cart_at.date_to
        ) {
          result.push({
            type: "user_last_open_cart_at",
            date_condition: "><",
            date: formatDateForApi(rules.user_last_open_cart_at.date_from),
            max_date: formatDateForApi(rules.user_last_open_cart_at.date_to),
          });
        }
      } else if (rules.user_last_open_cart_at.days) {
        result.push({
          type: "user_last_open_cart_at",
          date_condition: rules.user_last_open_cart_at.date_condition,
          date: getDaysAgoDate(
            parseInt(rules.user_last_open_cart_at.days) || 0,
          ),
        });
      }
    }
    return result;
  }, [rules]);

  const { data: audienceData, isLoading: isLoadingAudience } =
    useCampaignAudience(conditions, "email");

  const toggleRule = (key) =>
    setRules((prev) => ({
      ...prev,
      [key]: { ...prev[key], active: !prev[key].active },
    }));

  const applyTemplate = (templateId) => {
    const template = TEMPLATES.find((t) => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
      setSelectedTemplate(templateId);
    }
  };

  const handleClose = () => navigate("/campaigns");

  const generateHtmlBody = () => {
    const paragraphs = body.split("\n\n").filter((p) => p.trim());
    const htmlParagraphs = paragraphs
      .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
      .join("");
    const trackingUrl = `https://api.orderingplus.com/v400/en/luisv4plus/helpers/emails/click/mkt/{{tracking_id}}?next=${encodeURIComponent(targetUrl)}`;
    const buttonHtml = `<p style="text-align: center; margin-top: 20px;"><a href='${trackingUrl}' target='_blank'><button style='font-size: 14px; color: white; padding: 12px 24px; cursor: pointer; border-radius: 25px; background: ${buttonColor}; border: none;'>${buttonText}</button></a></p>`;
    return htmlParagraphs + buttonHtml;
  };

  const buildCampaignData = (status) => ({
    name: campaignName,
    contact_type: "email",
    audience_type: audienceType,
    enabled: isEnabled,
    status,
    conditions: JSON.stringify(conditions),
    contact_data: JSON.stringify({ title: subject, body: generateHtmlBody() }),
    ...(audienceType === "fixed" &&
      !isASAP &&
      scheduledAt && { scheduled_at: `${scheduledAt} 00:00:00` }),
    ...(audienceType === "dynamic" && endAt && { end_at: `${endAt} 00:00:00` }),
  });

  const handleSubmit = async () => {
    if (!campaignName.trim()) {
      toast.error(t("createPage.validation.campaignNameRequired"));
      return;
    }
    if (conditions.length === 0) {
      toast.error(t("createPage.validation.audienceRuleRequired"));
      return;
    }
    if (!subject.trim()) {
      toast.error(t("createPage.validation.subjectRequired"));
      return;
    }
    if (!body.trim()) {
      toast.error(t("createPage.validation.messageBodyRequired"));
      return;
    }
    try {
      const status =
        audienceType === "fixed"
          ? isASAP
            ? "pending"
            : "scheduled"
          : "pending";
      await createCampaignMutation.mutateAsync(buildCampaignData(status));
      toast.success(t("createPage.toasts.campaignCreated"));
      navigate("/campaigns");
    } catch (error) {
      toast.error(t("createPage.toasts.createFailed"), {
        description: error.message,
      });
    }
  };

  return (
    <div
      className="ordering-campaigns-surface ordering-campaign-create flex h-full flex-col"
      data-ordering-expression="product"
    >
      {/* Sticky Header */}
      <div className="bg-background border-border sticky -top-1 z-[var(--layer-sticky)] -mt-2 mb-6 border-b pt-2 pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleClose}
              aria-label={t("detail.actions.backToCampaigns")}
              className="text-muted-foreground hover:bg-card hover:text-foreground rounded-full p-2 transition-colors"
            >
              <Icon name="ArrowLeft" className="h-5 w-5" />
            </button>
            <div>
              <h2 className="text-foreground text-xl font-bold">
                {t("createPage.header.title")}
              </h2>
              <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                <span className="text-primary font-medium">
                  {t("createPage.header.steps.setup")}
                </span>
                <Icon name="ChevronRight" className="h-3 w-3" />
                <span className="text-primary font-medium">
                  {t("createPage.header.steps.audience")}
                </span>
                <Icon name="ChevronRight" className="h-3 w-3" />
                <span className="text-primary font-medium">
                  {t("createPage.header.steps.content")}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="default"
            type="button"
            onClick={handleSubmit}
            disabled={createCampaignMutation.isPending}
            aria-busy={createCampaignMutation.isPending}
            className="ml-0 flex w-full items-center justify-center gap-2 px-6 py-2 sm:ml-auto sm:w-auto"
          >
            {createCampaignMutation.isPending && (
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
            )}
            {t("actions.createCampaign")}
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-8 xl:flex-row">
        {/* Left Column - Scrollable */}
        <div className="flex-1 space-y-8 overflow-y-auto pb-10 xl:pr-2">
          {/* Section 1: General */}
          <section>
            <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
              <span className="bg-accent/20 text-primary flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold">
                1
              </span>
              {t("createPage.sections.generalDetails")}
            </h3>
            <div className="border-border bg-background space-y-5 rounded-xl border p-6">
              <div>
                <label className="text-muted-foreground mb-1.5 block text-sm font-medium">
                  {t("createPage.fields.campaignName.label")}
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder={t("createPage.fields.campaignName.placeholder")}
                  className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 outline-none focus:border-transparent focus:ring-2"
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-3 block text-sm font-medium">
                  {t("createPage.fields.communicationChannel.label")}
                </label>
                <div className="border-border bg-background flex items-center gap-4 rounded-xl border p-4">
                  <div className="bg-accent/10 text-primary flex h-12 w-12 items-center justify-center rounded-full">
                    <Icon name="Mail" className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-foreground font-medium">
                      {t(
                        "createPage.fields.communicationChannel.emailCampaign",
                      )}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {t("createPage.fields.communicationChannel.onlyChannel")}
                    </p>
                  </div>
                  <div className="ml-auto">
                    <span className="border-primary/30 bg-accent/20 text-primary rounded-full border px-3 py-1 text-xs font-bold">
                      {t("createPage.fields.communicationChannel.selected")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Audience */}
          <section>
            <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
              <span className="bg-accent/20 text-primary flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold">
                2
              </span>
              {t("createPage.sections.defineAudience")}
            </h3>
            <div className="border-border bg-background rounded-xl border p-6">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-muted-foreground text-sm">
                  {t("createPage.audience.subtitle")}
                </p>
                {conditions.length > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">
                      {t("createPage.audience.reaching")}
                    </span>
                    {isLoadingAudience ? (
                      <div className="border-primary h-4 w-4 animate-spin rounded-full border-b-2"></div>
                    ) : (
                      <span className="text-foreground font-medium">
                        {audienceData?.pagination?.total || 0}{" "}
                        {t("createPage.audience.people")}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {RULE_LIST.map((rule) => (
                  <div
                    key={rule.key}
                    className={`rounded-lg border transition-all ${
                      rules[rule.key]?.active
                        ? "border-primary bg-accent/5"
                        : "border-border bg-background/50"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleRule(rule.key)}
                      aria-pressed={Boolean(rules[rule.key]?.active)}
                      className="flex w-full items-center p-4 text-left"
                    >
                      <div
                        className={`mr-3 flex h-5 w-5 items-center justify-center rounded border transition-colors ${
                          rules[rule.key]?.active
                            ? "border-primary bg-primary"
                            : "border-border"
                        }`}
                      >
                        {rules[rule.key]?.active && (
                          <Icon
                            name={rule.icon}
                            className="h-3 w-3 text-white"
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <span
                          className={`font-medium ${
                            rules[rule.key]?.active
                              ? "text-white"
                              : "text-muted-foreground"
                          }`}
                        >
                          {rule.title}
                        </span>
                        <p className="text-muted-foreground text-xs">
                          {rule.description}
                        </p>
                      </div>
                    </button>
                    {rule.key === "orders_count" &&
                      rules.orders_count.active && (
                        <div className="flex items-center gap-3 px-4 pb-4 pl-12">
                          <NativeSelect
                            value={rules.orders_count.condition}
                            onChange={(e) =>
                              setRules({
                                ...rules,
                                orders_count: {
                                  ...rules.orders_count,
                                  condition: e.target.value,
                                },
                              })
                            }
                            className="custom-select border-border bg-card focus:ring-ring text-foreground rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                          >
                            <option value=">">
                              {t("createPage.audience.conditions.moreThan")}
                            </option>
                            <option value="<">
                              {t("createPage.audience.conditions.lessThan")}
                            </option>
                            <option value="=">
                              {t("createPage.audience.conditions.exactly")}
                            </option>
                          </NativeSelect>
                          <input
                            type="text"
                            value={rules.orders_count.value}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (
                                val === "" ||
                                (/^\d+$/.test(val) && parseInt(val) <= 999)
                              )
                                setRules({
                                  ...rules,
                                  orders_count: {
                                    ...rules.orders_count,
                                    value: val,
                                  },
                                });
                            }}
                            className="border-border bg-card focus:ring-ring text-foreground w-20 rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                            maxLength={3}
                          />
                          <span className="text-muted-foreground text-sm">
                            {t("createPage.audience.conditions.ordersPlaced")}
                          </span>
                        </div>
                      )}
                    {rule.key === "user_created_at" &&
                      rules.user_created_at.active && (
                        <div className="flex flex-wrap items-center gap-3 px-4 pb-4 pl-12">
                          <span className="text-muted-foreground text-sm">
                            {t("createPage.audience.conditions.registered")}
                          </span>
                          <NativeSelect
                            value={rules.user_created_at.date_condition}
                            onChange={(e) =>
                              setRules({
                                ...rules,
                                user_created_at: {
                                  ...rules.user_created_at,
                                  date_condition: e.target.value,
                                },
                              })
                            }
                            className="custom-select border-border bg-card focus:ring-ring text-foreground rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                          >
                            <option value="<">
                              {t("createPage.audience.conditions.lessThan")}
                            </option>
                            <option value=">">
                              {t("createPage.audience.conditions.moreThan")}
                            </option>
                            <option value="><">
                              {t("createPage.audience.conditions.betweenDates")}
                            </option>
                          </NativeSelect>
                          {rules.user_created_at.date_condition === "><" ? (
                            <>
                              <CampaignDatePicker
                                value={rules.user_created_at.date_from}
                                onChange={(val) =>
                                  setRules({
                                    ...rules,
                                    user_created_at: {
                                      ...rules.user_created_at,
                                      date_from: val,
                                    },
                                  })
                                }
                                placeholder={t(
                                  "createPage.audience.conditions.from",
                                )}
                              />
                              <span className="text-muted-foreground text-sm">
                                {t("createPage.audience.conditions.and")}
                              </span>
                              <CampaignDatePicker
                                value={rules.user_created_at.date_to}
                                onChange={(val) =>
                                  setRules({
                                    ...rules,
                                    user_created_at: {
                                      ...rules.user_created_at,
                                      date_to: val,
                                    },
                                  })
                                }
                                placeholder={t(
                                  "createPage.audience.conditions.to",
                                )}
                              />
                            </>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={rules.user_created_at.days}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (
                                    val === "" ||
                                    (/^\d+$/.test(val) && parseInt(val) <= 999)
                                  )
                                    setRules({
                                      ...rules,
                                      user_created_at: {
                                        ...rules.user_created_at,
                                        days: val,
                                      },
                                    });
                                }}
                                className="border-border bg-card focus:ring-ring text-foreground w-20 rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                                maxLength={3}
                              />
                              <span className="text-muted-foreground text-sm">
                                {t("createPage.audience.conditions.daysAgo")}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    {rule.key === "user_last_open_cart_at" &&
                      rules.user_last_open_cart_at.active && (
                        <div className="flex flex-wrap items-center gap-3 px-4 pb-4 pl-12">
                          <span className="text-muted-foreground text-sm">
                            {t("createPage.audience.conditions.cartOpened")}
                          </span>
                          <NativeSelect
                            value={rules.user_last_open_cart_at.date_condition}
                            onChange={(e) =>
                              setRules({
                                ...rules,
                                user_last_open_cart_at: {
                                  ...rules.user_last_open_cart_at,
                                  date_condition: e.target.value,
                                },
                              })
                            }
                            className="custom-select border-border bg-card focus:ring-ring text-foreground rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                          >
                            <option value="<">
                              {t("createPage.audience.conditions.lessThan")}
                            </option>
                            <option value=">">
                              {t("createPage.audience.conditions.moreThan")}
                            </option>
                            <option value="><">
                              {t("createPage.audience.conditions.betweenDates")}
                            </option>
                          </NativeSelect>
                          {rules.user_last_open_cart_at.date_condition ===
                          "><" ? (
                            <>
                              <CampaignDatePicker
                                value={rules.user_last_open_cart_at.date_from}
                                onChange={(val) =>
                                  setRules({
                                    ...rules,
                                    user_last_open_cart_at: {
                                      ...rules.user_last_open_cart_at,
                                      date_from: val,
                                    },
                                  })
                                }
                                placeholder={t(
                                  "createPage.audience.conditions.from",
                                )}
                              />
                              <span className="text-muted-foreground text-sm">
                                {t("createPage.audience.conditions.and")}
                              </span>
                              <CampaignDatePicker
                                value={rules.user_last_open_cart_at.date_to}
                                onChange={(val) =>
                                  setRules({
                                    ...rules,
                                    user_last_open_cart_at: {
                                      ...rules.user_last_open_cart_at,
                                      date_to: val,
                                    },
                                  })
                                }
                                placeholder={t(
                                  "createPage.audience.conditions.to",
                                )}
                              />
                            </>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={rules.user_last_open_cart_at.days}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (
                                    val === "" ||
                                    (/^\d+$/.test(val) && parseInt(val) <= 999)
                                  )
                                    setRules({
                                      ...rules,
                                      user_last_open_cart_at: {
                                        ...rules.user_last_open_cart_at,
                                        days: val,
                                      },
                                    });
                                }}
                                className="border-border bg-card focus:ring-ring text-foreground w-20 rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                                maxLength={3}
                              />
                              <span className="text-muted-foreground text-sm">
                                {t("createPage.audience.conditions.daysAgo")}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                  </div>
                ))}
              </div>
              {/* Audience Type */}
              <fieldset className="border-border mt-6 border-t pt-6">
                <legend className="text-muted-foreground mb-3 text-sm font-medium">
                  {t("createPage.audience.audienceType")}
                </legend>
                <div className="space-y-3">
                  <div
                    className={`rounded-lg border p-4 transition-all ${
                      audienceType === "fixed"
                        ? "border-primary bg-accent/5"
                        : "border-border bg-background/50"
                    }`}
                  >
                    <label className="focus-within:ring-ring flex min-h-11 cursor-pointer items-center gap-3 rounded focus-within:ring-2 focus-within:ring-offset-2">
                      <input
                        type="radio"
                        name="campaign-audience-type"
                        value="fixed"
                        checked={audienceType === "fixed"}
                        onChange={() => setAudienceType("fixed")}
                        className="sr-only"
                      />
                      <span
                        aria-hidden="true"
                        className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                          audienceType === "fixed"
                            ? "border-primary"
                            : "border-border"
                        }`}
                      >
                        {audienceType === "fixed" && (
                          <span className="bg-primary h-2 w-2 rounded-full" />
                        )}
                      </span>
                      <div>
                        <span className="text-foreground font-medium">
                          Fixed
                        </span>
                        <p className="text-muted-foreground text-xs">
                          {t("createPage.audience.fixedDescription")}
                        </p>
                      </div>
                    </label>
                    {audienceType === "fixed" && (
                      <fieldset className="mt-4 ml-7 space-y-3">
                        <legend className="text-muted-foreground text-sm font-medium">
                          {t("createPage.audience.deliverySchedule")}
                        </legend>
                        <label className="focus-within:ring-ring flex min-h-11 cursor-pointer items-center gap-2 rounded focus-within:ring-2 focus-within:ring-offset-2">
                          <input
                            type="radio"
                            name="campaign-delivery-schedule"
                            value="asap"
                            checked={isASAP}
                            onChange={() => setIsASAP(true)}
                            className="sr-only"
                          />
                          <span
                            aria-hidden="true"
                            className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                              isASAP ? "border-primary" : "border-border"
                            }`}
                          >
                            {isASAP && (
                              <span className="bg-primary h-2 w-2 rounded-full" />
                            )}
                          </span>
                          <span className="text-muted-foreground text-sm">
                            {t("createPage.audience.sendAsap")}
                          </span>
                        </label>
                        <label className="focus-within:ring-ring flex min-h-11 cursor-pointer items-center gap-2 rounded focus-within:ring-2 focus-within:ring-offset-2">
                          <input
                            type="radio"
                            name="campaign-delivery-schedule"
                            value="scheduled"
                            checked={!isASAP}
                            onChange={() => setIsASAP(false)}
                            className="sr-only"
                          />
                          <span
                            aria-hidden="true"
                            className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                              !isASAP ? "border-primary" : "border-border"
                            }`}
                          >
                            {!isASAP && (
                              <span className="bg-primary h-2 w-2 rounded-full" />
                            )}
                          </span>
                          <span className="text-muted-foreground text-sm">
                            {t("createPage.audience.scheduleForLater")}
                          </span>
                        </label>
                        {!isASAP && (
                          <div className="ml-6">
                            <CampaignDatePicker
                              value={scheduledAt}
                              onChange={setScheduledAt}
                              placeholder={t(
                                "createPage.audience.conditions.selectDate",
                              )}
                              minDate={new Date()}
                            />
                          </div>
                        )}
                      </fieldset>
                    )}
                  </div>
                  <div
                    className={`rounded-lg border p-4 transition-all ${
                      audienceType === "dynamic"
                        ? "border-primary bg-accent/5"
                        : "border-border bg-background/50"
                    }`}
                  >
                    <label className="focus-within:ring-ring flex min-h-11 cursor-pointer items-center gap-3 rounded focus-within:ring-2 focus-within:ring-offset-2">
                      <input
                        type="radio"
                        name="campaign-audience-type"
                        value="dynamic"
                        checked={audienceType === "dynamic"}
                        onChange={() => setAudienceType("dynamic")}
                        className="sr-only"
                      />
                      <span
                        aria-hidden="true"
                        className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                          audienceType === "dynamic"
                            ? "border-primary"
                            : "border-border"
                        }`}
                      >
                        {audienceType === "dynamic" && (
                          <span className="bg-primary h-2 w-2 rounded-full" />
                        )}
                      </span>
                      <div>
                        <span className="text-foreground font-medium">
                          Dynamic
                        </span>
                        <p className="text-muted-foreground text-xs">
                          {t("createPage.audience.dynamicDescription")}
                        </p>
                      </div>
                    </label>
                    {audienceType === "dynamic" && (
                      <div className="mt-4 ml-7 space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground text-sm">
                            {t("createPage.audience.enableCampaign")}
                          </span>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={setIsEnabled}
                            aria-label={t("createPage.audience.enableCampaign")}
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-muted-foreground text-sm">
                            {t("createPage.audience.endDate")}
                          </span>
                          <div>
                            <CampaignDatePicker
                              value={endAt}
                              onChange={setEndAt}
                              placeholder={t(
                                "createPage.audience.conditions.selectDate",
                              )}
                              minDate={new Date()}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </fieldset>
            </div>
          </section>

          {/* Section 3: Content */}
          <section>
            <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
              <span className="bg-accent/20 text-primary flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold">
                3
              </span>
              {t("createPage.sections.messageContent")}
            </h3>
            <div className="border-border bg-background space-y-6 rounded-xl border p-6">
              <div>
                <label className="text-muted-foreground mb-3 block flex items-center gap-2 text-sm font-medium">
                  <Icon name="LayoutTemplate" className="h-4 w-4" />
                  {t("createPage.content.chooseTemplate")}
                </label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {TEMPLATES.map((template) => (
                    <button
                      type="button"
                      key={template.id}
                      onClick={() => applyTemplate(template.id)}
                      aria-pressed={selectedTemplate === template.id}
                      className={`relative rounded-lg border p-3 text-left transition-all ${
                        selectedTemplate === template.id
                          ? "border-primary bg-accent/10 shadow-[var(--od-shadow-blue)] shadow-md"
                          : "border-border bg-background hover:border-border hover:bg-card"
                      }`}
                    >
                      <h4
                        className={`mb-1 text-sm font-semibold ${
                          selectedTemplate === template.id
                            ? "text-white"
                            : "text-muted-foreground"
                        }`}
                      >
                        {template.name}
                      </h4>
                      <p className="text-muted-foreground line-clamp-2 text-[10px]">
                        {template.subject}
                      </p>
                      {selectedTemplate === template.id && (
                        <div className="bg-primary absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full">
                          <Icon
                            name="Check"
                            className="h-2.5 w-2.5 text-white"
                          />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-card h-px w-full"></div>
              <div>
                <label className="text-muted-foreground mb-1.5 block text-sm font-medium">
                  {t("createPage.content.subjectLine")}
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t("createPage.content.subjectPlaceholder")}
                  className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 outline-none focus:border-transparent focus:ring-2"
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1.5 block text-sm font-medium">
                  {t("createPage.content.messageBody")}
                </label>
                <div className="relative">
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={8}
                    placeholder={t("createPage.content.messagePlaceholder")}
                    className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full resize-none rounded-lg border p-4 leading-relaxed outline-none focus:border-transparent focus:ring-2"
                  />
                  <div className="text-muted-foreground absolute right-3 bottom-3 text-xs">
                    {body.length} characters
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="text-muted-foreground mb-1.5 block flex items-center gap-2 text-sm font-medium">
                    <Icon name="Globe" className="h-3.5 w-3.5" />
                    {t("createPage.content.buttonDestination")}
                  </label>
                  <NativeSelect
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    className="custom-select border-border bg-card focus:ring-ring text-foreground w-full cursor-pointer rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                  >
                    {destinationUrls.map((url) => (
                      <option key={url} value={url}>
                        {url}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
                <div>
                  <label className="text-muted-foreground mb-2 block flex items-center gap-2 text-sm font-medium">
                    <Icon name="MousePointer" className="h-3.5 w-3.5" />
                    {t("createPage.content.callToActionStyle")}
                  </label>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      placeholder={t(
                        "createPage.content.buttonTextPlaceholder",
                      )}
                      className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 outline-none focus:ring-2"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground mr-1 text-xs">
                        {t("createPage.content.color")}
                      </span>
                      <div className="flex gap-2">
                        {PRESET_COLORS.map((color) => (
                          <button
                            type="button"
                            key={color}
                            onClick={() => setButtonColor(color)}
                            aria-label={color}
                            aria-pressed={buttonColor === color}
                            className={`h-8 w-8 rounded-full border-2 transition-all ${
                              buttonColor === color
                                ? "scale-110 border-white"
                                : "border-transparent hover:scale-105"
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                        <div className="border-border relative h-8 w-8 cursor-pointer overflow-hidden rounded-full border-2 transition-transform hover:scale-105">
                          <input
                            type="color"
                            value={buttonColor}
                            onChange={(e) => setButtonColor(e.target.value)}
                            className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                          />
                          <div className="from-destructive via-primary to-primary h-full w-full bg-gradient-to-r"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Right Column: Device Preview - Fixed position on desktop, below content on mobile */}
        <div className="ordering-generated-preview w-full shrink-0 pt-2 xl:sticky xl:top-16 xl:w-[350px] xl:self-start">
          <h3 className="mb-4 flex items-center justify-center gap-2 text-sm font-semibold tracking-wider text-gray-400 uppercase xl:justify-start">
            <Icon name="Smartphone" className="h-4 w-4" />{" "}
            {t("createPage.preview.devicePreview")}
          </h3>
          <div className="mx-auto max-w-[350px] rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2 xl:mx-0">
            <div className="relative">
              <div className="absolute top-0 left-1/2 z-10 flex h-6 w-24 -translate-x-1/2 transform items-center justify-center gap-1 rounded-b-xl bg-gray-950">
                <div className="h-2 w-2 rounded-full bg-gray-800"></div>
                <div className="h-2 w-2 rounded-full bg-gray-800"></div>
              </div>
              <div className="overflow-hidden rounded-[2rem] bg-white">
                <div className="flex items-center justify-between bg-gray-100 px-4 py-2 text-xs text-gray-600">
                  <span className="font-bold">9:41</span>
                  <div className="flex items-center gap-1">
                    <div className="h-2 w-4 rounded-sm bg-gray-400"></div>
                    <div className="h-2 w-4 rounded-sm bg-gray-400"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-blue-600 px-4 py-3">
                  <Icon name="ArrowLeft" className="h-5 w-5 text-white" />
                  <span className="font-medium text-white">
                    {t("createPage.preview.inbox")}
                  </span>
                  <Icon name="Search" className="h-5 w-5 text-white" />
                </div>
                <div className="min-h-[550px] p-4">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                      <span className="text-sm font-bold text-blue-600">O</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Ordering.co
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("createPage.preview.toMe")}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {t("createPage.preview.now")}
                    </span>
                  </div>
                  <h3 className="mb-3 text-sm leading-tight font-semibold text-gray-900">
                    {subject || t("createPage.preview.subjectFallback")}
                  </h3>
                  <div className="text-xs leading-relaxed whitespace-pre-line text-gray-600">
                    {body || t("createPage.preview.messageFallback")}
                  </div>
                  <div className="mt-6 border-t border-gray-100 pt-4 text-center">
                    <button
                      type="button"
                      onClick={() =>
                        targetUrl && window.open(targetUrl, "_blank")
                      }
                      className="inline-block max-w-[200px] cursor-pointer rounded-full px-8 py-3 text-xs font-bold text-white shadow-lg transition-opacity hover:opacity-90"
                      style={{ backgroundColor: buttonColor }}
                    >
                      <span
                        className="block overflow-hidden"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          lineHeight: "1.4",
                        }}
                      >
                        {buttonText || t("createPage.preview.shopNow")}
                      </span>
                    </button>
                    <p className="mt-4 text-[10px] text-gray-400">
                      {t("createPage.preview.buttonLinksTo")}{" "}
                      <span className="text-blue-600">{targetUrl}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignPage;
